import {Action, Selector, State, StateContext} from '@ngxs/store';
import {AppStateModel} from './appState.model';
import {RequestConfirmation, SetConfig, SetLanguage, StartLoading, StopLoading} from './actions/appState.actions';
import {Injectable, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {catchError, first, mergeMap, tap} from 'rxjs/operators';
import {UserService} from '../shared/services/user/user.service';
import {Router} from '@angular/router';
import {from, Observable, of} from 'rxjs';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AccountSettingsModalComponent} from '../shared/components/modals/account-settings/account-settings.modal.component';
import {CreateUserModalComponent} from '../shared/components/modals/create-user/create-user-modal.component';
import {UpdateUserModalComponent} from '../shared/components/modals/update-user/update-user-modal.component';
import {DeleteUserModalComponent} from '../shared/components/modals/delete-user/delete-user-modal.component';
import {ToastrService} from 'ngx-toastr';
import {UpdateCurrentUser, UpdateCurrentUserPassword, UserLogin, UserLogout, UserRestore} from './actions/appState.account.actions';
import {CreateUser, DeleteUser, GetUsers, UpdateUser, UpdateUserPassword} from './actions/appState.users.actions';
import {
  OpenModalAccountSettings,
  OpenModalPaycardDetail, OpenModalPaymentDetail, OpenModalPreorderDetail,
  OpenModalUserCreate,
  OpenModalUserDelete,
  OpenModalUserEdit, OpenModalWebchargeDetail
} from './actions/appState.modals.actions';
import {PaginatedResponseModel} from '../shared/models/paginated.response.model';
import {DeletePaycard, GetPaycardDetail, GetPaycards, ResetPaycardPin} from './actions/appState.paycards.actions';
import {PaycardService} from '../shared/services/paycard/paycard.service';
import {PaycardDetailModalComponent} from '../shared/components/modals/paycard-detail/paycard-detail-modal.component';
import {ConfirmModalComponent} from '../shared/components/modals/confirm/confirm-modal.component';
import {GetWebchargeDetail, GetWebcharges} from './actions/appState.webcharges.actions';
import {WebchargeService} from '../shared/services/webcharge/webcharge.service';
import {PreorderService} from '../shared/services/preorder/preorder.service';
import {GetPreorderDetail, GetPreorders} from './actions/appState.preorders.actions';
import {PreorderDetailModalComponent} from '../shared/components/modals/preorder-detail/preorder-detail-modal.component';
import {WebchargeDetailModalComponent} from '../shared/components/modals/webcharge-detail/webcharge-detail-modal.component';
import {PaymentDetailModalComponent} from '../shared/components/modals/payment-detail/payment-detail-modal.component';

const EMPTY_PAGINATION: PaginatedResponseModel<any> = {
  content: [],
  first: true,
  last: true,
  number: 0,
  numberOfElements: 0,
  totalPages: 0,
  size: 25,
  totalElements: 0,
  sort: {
    sorted: true,
    unsorted: false
  },
  pageable: {
    offset: 0,
    paged: true,
    pageNumber: 0,
    pageSize: 25,
    unpaged: false,
    sort: {
      sorted: true,
      unsorted: false
    }
  }
};

const DEFAULT_STATE: AppStateModel = {
  auth: null,
  config: null,
  currentLanguage: null,
  currentUser: null,
  loading: true,
  users: EMPTY_PAGINATION,
  paycards: EMPTY_PAGINATION,
  webcharges: EMPTY_PAGINATION,
  preorders: EMPTY_PAGINATION,
  currentPaycard: null,
  currentWebcharge: null,
  currentPreorder: null
};

@State<AppStateModel>({
  defaults: DEFAULT_STATE,
  name: 'AppState'
})
@Injectable()
export class AppState {
  private readonly CREDENTIALS_KEY = 'PROFIPAY_GATEWAY_CREDENTIALS';

  // region selectors

  @Selector()
  static currentLanguage$(state: AppStateModel) {
    return state.currentLanguage;
  }

  @Selector()
  static currentUser$(state: AppStateModel) {
    return state.currentUser;
  }

  @Selector()
  static config$(state: AppStateModel) {
    return state.config;
  }

  @Selector()
  static auth$(state: AppStateModel) {
    return state.auth;
  }

  @Selector()
  static loading$(state: AppStateModel) {
    return state.loading;
  }

  @Selector()
  static users$(state: AppStateModel) {
    return state.users;
  }

  @Selector()
  static paycards$(state: AppStateModel) {
    return state.paycards;
  }

  @Selector()
  static currentPaycard$(state: AppStateModel) {
    return state.currentPaycard;
  }

  @Selector()
  static preorders$(state: AppStateModel) {
    return state.preorders;
  }

  @Selector()
  static currentPreorder$(state: AppStateModel) {
    return state.currentPreorder;
  }

  @Selector()
  static webcharges$(state: AppStateModel) {
    return state.webcharges;
  }

  @Selector()
  static currentWebcharge$(state: AppStateModel) {
    return state.currentWebcharge;
  }

  // endregion

  constructor(
    private translate: TranslateService,
    private userService: UserService,
    private paycardService: PaycardService,
    private webchargeService: WebchargeService,
    private preorderService: PreorderService,
    private router: Router,
    private ngZone: NgZone,
    private ngbModal: NgbModal,
    private toastr: ToastrService
  ) {
  }

  // region app config

  @Action(StartLoading)
  private startLoading(ctx: StateContext<AppStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      loading: true
    });
  }

  @Action(StopLoading)
  private stopLoading(ctx: StateContext<AppStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      loading: false
    });
  }

  @Action(SetLanguage)
  private setLanguage(ctx: StateContext<AppStateModel>, action: SetLanguage) {
    const language = action.language;
    return this.translate.use(language).pipe(
      tap(() => ctx.setState({...ctx.getState(), currentLanguage: language}))
    );
  }

  @Action(SetConfig)
  private setConfig(ctx: StateContext<AppStateModel>, action: SetConfig) {
    ctx.setState({
      ...ctx.getState(),
      config: action.config
    });

    return ctx.dispatch(new SetLanguage(action.config.adminUi.defaultLanguage));
  }

  // endregion

  // region account

  @Action(UserLogin)
  private login(ctx: StateContext<AppStateModel>, action: UserLogin) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => {
        ctx.setState({
          ...ctx.getState(),
          auth: action.basicAuth
        });
        return this.userService.getCurrent();
      }),
      tap(currentUser => {
        ctx.setState({
          ...ctx.getState(),
          currentUser
        });
        this.saveCredentials(action.basicAuth);
      }),
      mergeMap(() =>
        ctx.dispatch(new StopLoading())
      ),
      catchError((error) => {
        console.log(`could not login user`);
        return this.translate.get(['errors.login-failed.description', 'errors.login-failed.title']).pipe(
          first(),
          mergeMap((result) => {
            console.log(result);
            this.toastr.error(
              this.translate.instant(result['errors.login-failed.description']),
              this.translate.instant(result['errors.login-failed.title']),
            );
            return ctx.dispatch(new UserLogout());
          })
        );
      })
    );
  }

  @Action(UserLogout)
  private logout(ctx: StateContext<AppStateModel>, action: UserLogout) {
    this.deleteCredentials();

    ctx.setState({
      ...DEFAULT_STATE,
      config: ctx.getState().config
    });

    this.ngbModal.dismissAll();

    return from(
      this.ngZone.run(
        () => this.router.navigate(['login'])
      )
    ).pipe(
      mergeMap(() => ctx.dispatch(new StopLoading()))
    );
  }

  @Action(UserRestore)
  private restore(ctx: StateContext<AppStateModel>) {
    const authString = this.getCredentials();
    return authString ? ctx.dispatch(new UserLogin(authString)) : ctx.dispatch(new StopLoading());
  }

  @Action(UpdateCurrentUser)
  private updateCurrent(ctx: StateContext<AppStateModel>, action: UpdateCurrentUser) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.updateCurrent(action.user)),
      mergeMap(() => ctx.dispatch(new UserLogout())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(UpdateCurrentUserPassword)
  private updateCurrentPassword(ctx: StateContext<AppStateModel>, action: UpdateCurrentUserPassword) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.updateCurrentPassword(action.password)),
      mergeMap(() => ctx.dispatch(new UserLogout())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  // endregion

  // region users

  @Action(GetUsers)
  private getUsers(ctx: StateContext<AppStateModel>, action: GetUsers) {
    const {page, size} = this.getPaginationParams(ctx.getState().users, action);

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.getPaginated(page, size, action.userName)),
      tap(users => ctx.setState({...ctx.getState(), users})),
      mergeMap(() => ctx.dispatch(new StopLoading())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(CreateUser)
  private createUser(ctx: StateContext<AppStateModel>, action: CreateUser) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.create(action.user)),
      tap(() => {
        this.toastr.success(
          this.translate.instant('information.user-created.description', {userName: action.user.userName}),
          this.translate.instant('information.user-created.title')
        );
      }),
      mergeMap(() => ctx.dispatch(new GetUsers())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(DeleteUser)
  private deleteUser(ctx: StateContext<AppStateModel>, action: DeleteUser) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.delete(action.userName)),
      tap(() => {
        this.toastr.success(
          this.translate.instant('information.user-deleted.description', {userName: action.userName}),
          this.translate.instant('information.user-deleted.title')
        );
      }),
      mergeMap(() => ctx.dispatch(new GetUsers())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(UpdateUser)
  private updateUser(ctx: StateContext<AppStateModel>, action: UpdateUser) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.update(action.user, action.userName)),
      tap(() => {
        this.toastr.success(
          this.translate.instant('information.user-updated.description', {userName: action.user.userName}),
          this.translate.instant('information.user-updated.title')
        );
      }),
      mergeMap(() => ctx.dispatch(new GetUsers())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(UpdateUserPassword)
  private updateUserPassword(ctx: StateContext<AppStateModel>, action: UpdateUserPassword) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.userService.updatePassword(action.password, action.userName)),
      mergeMap(() => ctx.dispatch(new StopLoading())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  // endregion

  // region modals

  @Action(RequestConfirmation)
  private openConfirmationModal(ctx: StateContext<AppStateModel>, action: RequestConfirmation) {
    return this.ngZone.run(() => {
      const ngbModalRef = this.ngbModal.open(ConfirmModalComponent);
      ngbModalRef.componentInstance.text = this.translate.instant(action.i18nKey, action.variables);
      return ngbModalRef.result;
    });
  }

  @Action(OpenModalAccountSettings)
  private openAccountModal(ctx: StateContext<AppStateModel>) {
    this.ngbModal.dismissAll();
    const ngbModalRef = this.ngbModal.open(AccountSettingsModalComponent);
    (ngbModalRef.componentInstance as AccountSettingsModalComponent).user = ctx.getState().currentUser;
  }

  @Action(OpenModalUserCreate)
  private openUserCreateModal(ctx: StateContext<AppStateModel>) {
    this.ngbModal.dismissAll();
    const ngbModalRef = this.ngbModal.open(CreateUserModalComponent);
  }

  @Action(OpenModalUserEdit)
  private openUserEditModal(ctx: StateContext<AppStateModel>, action: OpenModalUserEdit) {
    this.ngbModal.dismissAll();
    const ngbModalRef = this.ngbModal.open(UpdateUserModalComponent);
    (ngbModalRef.componentInstance as UpdateUserModalComponent).user = action.user;
  }

  @Action(OpenModalUserDelete)
  private openUserDeleteModal(ctx: StateContext<AppStateModel>, action: OpenModalUserDelete) {
    this.ngbModal.dismissAll();
    const ngbModalRef = this.ngbModal.open(DeleteUserModalComponent);
    (ngbModalRef.componentInstance as DeleteUserModalComponent).userName = action.userName;
  }

  @Action(OpenModalPaycardDetail)
  private openPaycardDetailModal(ctx: StateContext<AppStateModel>, action: OpenModalPaycardDetail) {
    return ctx.dispatch(new GetPaycardDetail(action.paycardId)).pipe(
      tap(() => {
        this.ngbModal.dismissAll();
        const ngbModalRef = this.ngbModal.open(PaycardDetailModalComponent, {size: 'l'});
        const modalComponent = (ngbModalRef.componentInstance as PaycardDetailModalComponent);
        modalComponent.paycard = ctx.getState().currentPaycard;
        modalComponent.paycardId = action.paycardId;
      })
    );
  }

  @Action(OpenModalWebchargeDetail)
  private openWebchargeDetailModal(ctx: StateContext<AppStateModel>, action: OpenModalWebchargeDetail) {
    return ctx.dispatch(new GetWebchargeDetail(action.reference)).pipe(
      tap(() => {
        this.ngbModal.dismissAll();
        const ngbModalRef = this.ngbModal.open(WebchargeDetailModalComponent, {size: 'l'});
        (ngbModalRef.componentInstance as WebchargeDetailModalComponent).webcharge = ctx.getState().currentWebcharge;
      })
    );
  }

  @Action(OpenModalPreorderDetail)
  private openPreorderDetailModal(ctx: StateContext<AppStateModel>, action: OpenModalPreorderDetail) {
    return ctx.dispatch(new GetPreorderDetail(action.reference)).pipe(
      tap(() => {
        this.ngbModal.dismissAll();
        const ngbModalRef = this.ngbModal.open(PreorderDetailModalComponent, {size: 'xl'});
        (ngbModalRef.componentInstance as PreorderDetailModalComponent).preorder = ctx.getState().currentPreorder;
      })
    );
  }

  @Action(OpenModalPaymentDetail)
  private openPaymentDetailModal(ctx: StateContext<AppStateModel>, action: OpenModalPaymentDetail) {
    const ngbModalRef = this.ngbModal.open(PaymentDetailModalComponent, {size: 'xl'});
    (ngbModalRef.componentInstance as PaymentDetailModalComponent).payment = action.payment;
  }

  // endregion

  // region paycards

  @Action(GetPaycards)
  private getPaycards(ctx: StateContext<AppStateModel>, action: GetPaycards) {
    const {page, size} = this.getPaginationParams(ctx.getState().paycards, action);

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.paycardService.getPaginated(page, size, action.paycardId, action.status)),
      tap(paycards => ctx.setState({...ctx.getState(), paycards})),
      mergeMap(() => ctx.dispatch(new StopLoading())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(GetPaycardDetail)
  private getPaycardDetails(ctx: StateContext<AppStateModel>, action: GetPaycardDetail) {
    if (action.paycardId) {
      return ctx.dispatch(new StartLoading()).pipe(
        mergeMap(() => this.paycardService.getDetailsById(action.paycardId)),
        tap(currentPaycard => ctx.setState({...ctx.getState(), currentPaycard})),
        mergeMap(() => ctx.dispatch(new StopLoading())),
        catchError((error) => this.handleError(ctx, error))
      );
    } else {
      ctx.setState({...ctx.getState(), currentPaycard: null});
    }
  }

  @Action(ResetPaycardPin)
  private resetPaycardPin(ctx: StateContext<AppStateModel>, action: ResetPaycardPin) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.paycardService.resetPinById(action.paycardId)),
      tap(() => {
        this.toastr.success(
          this.translate.instant('information.paycard-pin-reset.description', {paycardId: action.paycardId}),
          this.translate.instant('information.paycard-pin-reset.title')
        );
      }),
      mergeMap(() => ctx.dispatch(new GetPaycards())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(DeletePaycard)
  private deletePaycard(ctx: StateContext<AppStateModel>, action: DeletePaycard) {
    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.paycardService.deleteById(action.paycardId)),
      tap(() => {
        const state = ctx.getState();
        if (state && state.currentPaycard && state.currentPaycard.paycardId === action.paycardId) {
          ctx.setState({
            ...ctx.getState(),
            currentPaycard: null
          });
        }
      }),
      tap(() => {
        this.toastr.success(
          this.translate.instant('information.paycard-delete.description', {paycardId: action.paycardId}),
          this.translate.instant('information.paycard-delete.title')
        );
      }),
      mergeMap(() => ctx.dispatch(new GetPaycards())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  // endregion

  // region webcharge

  @Action(GetWebcharges)
  private getWebcharges(ctx: StateContext<AppStateModel>, action: GetWebcharges) {
    const {page, size} = this.getPaginationParams(ctx.getState().webcharges, action);

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => this.webchargeService.getPaginated(page, size, action.reference, action.cardNumber, action.status)),
      tap(webcharges => ctx.setState({...ctx.getState(), webcharges})),
      mergeMap(() => ctx.dispatch(new StopLoading())),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(GetWebchargeDetail)
  private getWebchargeDetails(ctx: StateContext<AppStateModel>, action: GetWebchargeDetail) {
    if (action.reference) {
      return ctx.dispatch(new StartLoading()).pipe(
        mergeMap(() => this.webchargeService.getDetailsById(action.reference)),
        tap(currentWebcharge => ctx.setState({...ctx.getState(), currentWebcharge})),
        mergeMap(() => ctx.dispatch(new StopLoading())),
        catchError((error) => this.handleError(ctx, error))
      );
    } else {
      ctx.setState({...ctx.getState(), currentWebcharge: null});
    }
  }

  // endregion

  // region preorder

  @Action(GetPreorders)
  private getPreorders(ctx: StateContext<AppStateModel>, action: GetPreorders) {
    const {page, size} = this.getPaginationParams(ctx.getState().preorders, action);

    return ctx.dispatch(new StartLoading()).pipe(
      mergeMap(() => {
        return this.preorderService.getPaginated(page, size, action.reference, action.status);
      }),
      tap(preorders => {
        ctx.setState({...ctx.getState(), preorders});
      }),
      mergeMap(() => {
        return ctx.dispatch(new StopLoading());
      }),
      catchError((error) => this.handleError(ctx, error))
    );
  }

  @Action(GetPreorderDetail)
  private getPreorderDetails(ctx: StateContext<AppStateModel>, action: GetPreorderDetail) {
    if (action.reference) {
      return ctx.dispatch(new StartLoading()).pipe(
        mergeMap(() => {
          return this.preorderService.getDetailsById(action.reference);
        }),
        tap(currentPreorder => {
          return ctx.setState({...ctx.getState(), currentPreorder});
        }),
        mergeMap(() => {
          return ctx.dispatch(new StopLoading());
        }),
        catchError((error) => this.handleError(ctx, error))
      );
    } else {
      ctx.setState({...ctx.getState(), currentPreorder: null});
    }
  }

  // endregion

  // region helper functions

  private saveCredentials(auth: string) {
    window.localStorage.setItem(this.CREDENTIALS_KEY, auth);
  }

  private deleteCredentials() {
    window.localStorage.removeItem(this.CREDENTIALS_KEY);
  }

  private getCredentials() {
    return window.localStorage.getItem(this.CREDENTIALS_KEY);
  }

  private getPaginationParams(current: PaginatedResponseModel<any>, action: any): { page: number, size: number } {
    const page = action && action.page != null ? action.page : current.number;
    const size = action && action.size != null ? action.size : current.size;

    return {page, size};
  }

  private handleError(ctx: StateContext<AppStateModel>, error: any): Observable<any> {
    if (error && error.error && error.error.message) {
      this.toastr.error(
        this.translate.instant('errors.' + error.error.message + '.description'),
        this.translate.instant('errors.' + error.error.message + '.title')
      );
    } else {
      this.toastr.error(
        this.translate.instant('errors.unknown-error.description'),
        this.translate.instant('errors.unknown-error.title')
      );
    }
    return ctx.dispatch(new StopLoading());
  }

  // endregion
}
